import { addMonths, format } from 'date-fns';
import { ProofUploadWindowConfig } from './types';

/**
 * Status normalization map - maps PARTIALLY_APPROVED to APPROVED for UI display
 */
export const STATUS_DISPLAY_MAP: Record<string, string> = {
  APPROVED: 'APPROVED',
  PARTIALLY_APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  PENDING: 'PENDING',
};

/**
 * Check if a claim is partially approved
 */
export const isPartiallyApproved = (status: string): boolean => {
  return status === 'PARTIALLY_APPROVED';
};

/**
 * Get the badge color based on claim status
 * Treats PARTIALLY_APPROVED as APPROVED for color consistency
 */
export const getStatusBadgeColor = (
  status: string,
): 'information' | 'positive' | 'negative' | 'notice' => {
  const displayStatus = STATUS_DISPLAY_MAP[status] || status;
  switch (displayStatus) {
    case 'APPROVED':
      return 'positive';
    case 'REJECTED':
      return 'negative';
    case 'PENDING':
    default:
      return 'information';
  }
};

/**
 * Get the status label with proper capitalization
 * Treats PARTIALLY_APPROVED as APPROVED for display
 */
export const getStatusLabel = (status: string): string => {
  const displayStatus = STATUS_DISPLAY_MAP[status] || 'PENDING';
  return displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1).toLowerCase();
};

/**
 * Get the status color for detail view (similar to badge color)
 * Treats PARTIALLY_APPROVED as APPROVED for color consistency
 */
export const getStatusColor = (
  status: string,
): 'positive' | 'negative' | 'information' | 'notice' => {
  const displayStatus = STATUS_DISPLAY_MAP[status] || status;
  switch (displayStatus) {
    case 'APPROVED':
      return 'positive';
    case 'REJECTED':
      return 'negative';
    default:
      return 'information';
  }
};

/**
 * Format Unix timestamp to readable date string
 */
export const formatDate = (timestamp: number): string => {
  const date = new Date(Number(timestamp) * 1000);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const generateFile = (
  fileSize: number,
  fileName: string,
  fileType: string,
  url: string,
  fileId: string,
) => {
  const size = fileSize;

  const arr = new Uint8Array(size);

  const blob = new Blob([arr], { type: fileType || 'application/octet-stream' });
  const file = new File([blob], fileName.replace('ufh-default/', ''), {
    type: fileType || 'application/octet-stream',
  });

  return Object.assign(file, {
    id: fileId,
    status: 'success' as const,
    previewUrl: url,
    showPreview: true,
    progress: 100,
    url,
  });
};

export const getNextWindowStartDateLabel = (config?: ProofUploadWindowConfig | null): string => {
  if (!config) {
    return '';
  }
  if (config.active_key === 'every_month_at_period') {
    const startDay = config.values?.every_month_at_period?.number_range_value?.value?.start || 0;

    const now = new Date();
    const currentMonthCandidate = new Date(now.getFullYear(), now.getMonth(), startDay);

    return format(addMonths(currentMonthCandidate, 1), 'dd MMMM yyyy');
  }

  if (config.active_key === 'custom_range') {
    const customValues = config.values?.custom_range?.date_ranges_value?.values;
    if (!customValues?.length) {
      return '';
    }

    const todayTimestamp = Math.floor(Date.now() / 1000);
    const futureStartDates = customValues
      .map((value) => value.start ?? Infinity)
      .filter((start) => start > todayTimestamp);

    if (!futureStartDates.length) {
      return '';
    }

    const earliestStartTimestamp = Math.min(...futureStartDates);

    return format(new Date(earliestStartTimestamp * 1000), 'dd MMMM yyyy');
  }

  return '';
};
